<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'

import type { MiniGame, TeamStructure, Theme } from '~/stores/challengeWheel'

import { BOYS_GIRLS_TEAMS, COLOR_TEAMS, CUSTOM_COLORS, MINI_GAME_CONFIG } from '~/stores/challengeWheel'

const props = withDefaults(defineProps<{
  pairingCode?: string
  pairingUrl?: string
  peerPresentersCount?: number
  peerReady?: boolean
  popupPresenterConnected?: boolean
  presentationConnecting?: boolean
  presentationSupported?: boolean
  presenterEntryUrl?: string
}>(), {
  pairingCode: '',
  pairingUrl: '',
  peerPresentersCount: 0,
  peerReady: false,
  popupPresenterConnected: false,
  presentationConnecting: false,
  presentationSupported: true,
  presenterEntryUrl: '',
})

const emit = defineEmits<{
  connectPresentationWindow: []
  disconnectPeerPresenters: []
  disconnectPresentation: []
  launchDisplayMode: []
  regeneratePairingCode: []
  start: []
}>()

const store = useChallengeWheelStore()
const { data: ui } = useChallengeWheelUI()

const presentationTabs = computed(() => [
  {
    icon: 'mdi:monitor',
    label: ui.value?.setup.presentation.tabThisDevice,
    slot: 'this-device' as const,
    value: 'this-device',
  },
  {
    icon: 'mdi:cellphone-link',
    label: ui.value?.setup.presentation.tabRemoteDisplay,
    slot: 'remote-display' as const,
    value: 'remote-display',
  },
  {
    icon: 'mdi:television-play',
    label: ui.value?.setup.presentation.tabUseAsDisplay,
    slot: 'use-as-display' as const,
    value: 'use-as-display',
  },
])

// ── Connection status pills (consistent across tabs) ───────────
type StatusPill = { icon: string, label: string, tone: StatusTone }
type StatusTone = 'connected' | 'connecting' | 'error' | 'idle'

const popupStatus = computed<StatusPill>(() => {
  const strings = ui.value?.setup.presentation
  if (!props.presentationSupported) {
    return { icon: 'mdi:alert-circle-outline', label: strings?.pillUnsupported ?? '', tone: 'error' }
  }
  if (props.popupPresenterConnected) {
    return { icon: 'mdi:check-circle', label: strings?.pillConnected ?? '', tone: 'connected' }
  }
  if (props.presentationConnecting) {
    return { icon: 'mdi:progress-clock', label: strings?.pillConnecting ?? '', tone: 'connecting' }
  }
  return { icon: 'mdi:circle-outline', label: strings?.pillIdle ?? '', tone: 'idle' }
})

const peerStatus = computed<StatusPill>(() => {
  const strings = ui.value?.setup.presentation
  if (!props.presentationSupported) {
    return { icon: 'mdi:alert-circle-outline', label: strings?.pillUnsupported ?? '', tone: 'error' }
  }
  if (props.peerPresentersCount > 0) {
    const label = props.peerPresentersCount === 1
      ? strings?.pairingPeerConnectedOne ?? ''
      : (strings?.pairingPeerConnectedMany ?? '').replace('{count}', String(props.peerPresentersCount))
    return { icon: 'mdi:check-circle', label, tone: 'connected' }
  }
  if (!props.peerReady) {
    return { icon: 'mdi:progress-clock', label: strings?.pairingPeerConnecting ?? '', tone: 'connecting' }
  }
  return { icon: 'mdi:circle-outline', label: strings?.pairingPeerReady ?? '', tone: 'idle' }
})

// Brand palette is yellow/red/ink/chalk only, so Nuxt UI's `success` resolves
// to brand yellow and `warning` to brand red. For the pill semantics we want
// the standard green-for-good / amber-for-pending vocabulary, so the
// connected and connecting tones override with concrete Tailwind colors.
const pillToneClasses: Record<StatusTone, string> = {
  connected: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700',
  connecting: 'border-amber-500/40 bg-amber-500/10 text-amber-700',
  error: 'border-red-500/50 bg-red-500/10 text-red-700',
  idle: 'border-default bg-elevated/50 text-muted',
}

// ── Option lists driven by content ──────────────────────
const themes = computed(() => ui.value?.setup.theme.options ?? [])
const teamStructures = computed(() => ui.value?.setup.teamStructure.structures ?? [])
const allMiniGames = computed(() => ui.value?.setup.miniGames.games ?? [])
const teamNameLabel = computed(() => ui.value?.setup.teamStructure.teamNameLabel ?? 'Team')

// ── Persisted setup options ──────────────────────────────
const theme = useLocalStorage<Theme>('sfg.challenge-wheel.theme', 'bible')
const teamStructure = useLocalStorage<TeamStructure>('sfg.challenge-wheel.teamStructure', 'color-teams')
const customTeamCount = useLocalStorage<number>('sfg.challenge-wheel.customTeamCount', 4)
const customTeamNames = useLocalStorage<string[]>(
  'sfg.challenge-wheel.customTeamNames',
  Array.from({ length: 6 }, (_, i) => `${teamNameLabel.value} ${i + 1}`),
)
const selectedGames = useLocalStorage<MiniGame[]>(
  'sfg.challenge-wheel.selectedGames',
  [ 'sword-drill', 'bible-trivia', 'categories', 'word-scramble' ],
)

const teamCountOptions = [ 2, 3, 4, 5, 6 ]

// ── Helpers ──────────────────────────────────────────────
const MIN_SELECTED_GAMES = 2

function toggleGame(game: MiniGame) {
  const current = selectedGames.value
  const idx = current.indexOf(game)
  if (idx >= 0) {
    selectedGames.value = current.filter(g => g !== game)
  } else {
    selectedGames.value = [ ...current, game ]
  }
}

const isGameSelected = (g: MiniGame) => selectedGames.value.includes(g)
const canStart = computed(() => selectedGames.value.length >= MIN_SELECTED_GAMES)

const colorTeamsPreview = COLOR_TEAMS
const boysGirlsPreview = BOYS_GIRLS_TEAMS

// ── Reset handler ─────────────────────────────────────
function handleReset() {
  theme.value = 'bible'
  teamStructure.value = 'color-teams'
  customTeamCount.value = 4
  customTeamNames.value = Array.from({ length: 6 }, (_, i) => `${teamNameLabel.value} ${i + 1}`)
  selectedGames.value = [ 'sword-drill', 'bible-trivia', 'categories', 'word-scramble' ]
}

defineExpose({ handleReset })

// ── Start handler ──────────────────────────────────────
function handleStart() {
  store.config.theme = theme.value
  store.config.teamStructure = teamStructure.value
  store.config.customTeamCount = customTeamCount.value
  store.config.customTeamNames = [ ...customTeamNames.value ]
  store.config.selectedGames = [ ...selectedGames.value ]
  emit('start')
}
</script>

<template>
  <div class="space-y-8">
    <!-- ── Theme ──────────────────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-9 shrink-0"
            name="mdi:palette-outline"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.theme.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.theme.description }}
            </p>
          </div>
        </div>
      </template>
      <div class="flex flex-wrap gap-3">
        <SfgOptionCard
          v-for="t in themes"
          :key="t.value"
          :selected="theme === t.value"
          class="w-auto!"
          @click="theme = t.value"
        >
          <div class="flex items-center gap-2.5 px-3 py-2.5">
            <UIcon
              :name="t.icon"
              class="size-5 shrink-0"
            />
            <span class="font-display uppercase tracking-[0.08em] text-[12px]">{{ t.label }}</span>
          </div>
        </SfgOptionCard>
      </div>
    </UCard>

    <!-- ── Team Structure ─────────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-9 shrink-0"
            name="mdi:account-group-outline"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.teamStructure.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.teamStructure.description }}
            </p>
          </div>
        </div>
      </template>

      <div class="space-y-4">
        <!-- Structure selector -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SfgOptionCard
            v-for="ts in teamStructures"
            :key="ts.value"
            :selected="teamStructure === ts.value"
            @click="teamStructure = ts.value"
          >
            <div class="px-4 py-3">
              <div class="font-semibold">
                {{ ts.label }}
              </div>
              <div class="mt-0.5 text-xs text-brand-ink/70">
                {{ ts.description }}
              </div>
            </div>
          </SfgOptionCard>
        </div>

        <!-- Color Teams preview -->
        <div
          v-if="teamStructure === 'color-teams'"
          class="flex flex-wrap gap-2 pt-1"
        >
          <div
            v-for="team in colorTeamsPreview"
            :key="team.name"
            :style="{ backgroundColor: team.color }"
            class="rounded-lg px-4 py-2 text-sm font-bold text-white shadow"
          >
            {{ team.name }}
          </div>
        </div>

        <!-- Boys v Girls preview -->
        <div
          v-else-if="teamStructure === 'boys-v-girls'"
          class="flex flex-wrap gap-2 pt-1"
        >
          <div
            v-for="team in boysGirlsPreview"
            :key="team.name"
            :style="{ backgroundColor: team.color }"
            class="rounded-lg px-4 py-2 text-sm font-bold text-white shadow"
          >
            {{ team.name }}
          </div>
        </div>

        <!-- Custom options -->
        <div
          v-else-if="teamStructure === 'custom'"
          class="space-y-4 pt-2"
        >
          <!-- Team count -->
          <div class="flex flex-wrap items-center gap-3">
            <span class="min-w-32 text-sm font-medium text-muted">{{ ui?.setup.teamStructure.numberOfTeamsLabel }}</span>
            <div class="flex flex-wrap gap-2">
              <SfgOptionCard
                v-for="n in teamCountOptions"
                :key="n"
                :selected="customTeamCount === n"
                class="w-auto!"
                @click="customTeamCount = n"
              >
                <span class="block px-4 py-2 text-sm font-semibold tabular-nums">{{ n }}</span>
              </SfgOptionCard>
            </div>
          </div>

          <!-- Team name inputs -->
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <UFormField
              v-for="i in customTeamCount"
              :key="i"
              :label="`${teamNameLabel} ${i}`"
            >
              <div class="flex items-center gap-2">
                <span
                  :style="{ backgroundColor: CUSTOM_COLORS[(i - 1) % CUSTOM_COLORS.length] }"
                  class="size-3 shrink-0 rounded-full"
                />
                <UInput
                  v-model="customTeamNames[i - 1]"
                  :placeholder="`${teamNameLabel} ${i}`"
                  class="w-full"
                />
              </div>
            </UFormField>
          </div>
        </div>
      </div>
    </UCard>

    <!-- ── Mini-game Selection ────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-9 shrink-0"
            name="mdi:gamepad-variant-outline"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.miniGames.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.miniGames.description }}
            </p>
          </div>
        </div>
      </template>

      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SfgOptionCard
          v-for="game in allMiniGames"
          :key="game.value"
          :selected="isGameSelected(game.value)"
          @click="toggleGame(game.value)"
        >
          <div class="flex flex-col items-center gap-2 px-4 py-5 text-center">
            <UIcon
              :name="game.icon"
              :style="{ color: MINI_GAME_CONFIG[game.value].color }"
              class="size-8"
            />
            <span class="text-sm font-semibold">{{ game.label }}</span>
          </div>
        </SfgOptionCard>
      </div>
    </UCard>

    <!-- ── Presentation Screen ──────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-9 shrink-0"
            name="mdi:presentation-play"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.presentation.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.presentation.description }}
            </p>
          </div>
        </div>
      </template>

      <UTabs
        :default-value="'this-device'"
        :items="presentationTabs"
        :ui="{ list: 'mb-4' }"
        variant="link"
      >
        <template #this-device>
          <div class="space-y-4">
            <p class="text-sm text-muted">
              {{ ui?.setup.presentation.thisDeviceHint }}
            </p>
            <div class="flex items-center gap-2 flex-wrap">
              <span
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium leading-none"
                :class="pillToneClasses[popupStatus.tone]"
              >
                <UIcon :name="popupStatus.icon" class="size-3.5"/>
                {{ popupStatus.label }}
              </span>
            </div>
            <UButton
              v-if="props.popupPresenterConnected"
              block
              color="warning"
              icon="mdi:close-box-multiple-outline"
              :label="ui?.setup.presentation.disconnectButton"
              size="lg"
              variant="outline"
              @click="emit('disconnectPresentation')"
            />
            <UButton
              v-else
              block
              :disabled="!props.presentationSupported"
              icon="mdi:open-in-new"
              :label="ui?.setup.presentation.connectButton"
              :loading="props.presentationConnecting"
              size="lg"
              @click="emit('connectPresentationWindow')"
            />
          </div>
        </template>

        <template #remote-display>
          <div class="space-y-4">
            <p class="text-sm text-muted">
              {{ ui?.setup.presentation.pairingDescription }}
            </p>
            <div class="flex items-center gap-2 flex-wrap">
              <span
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium leading-none"
                :class="pillToneClasses[peerStatus.tone]"
              >
                <UIcon :name="peerStatus.icon" class="size-3.5"/>
                {{ peerStatus.label }}
              </span>
              <UButton
                v-if="props.peerPresentersCount > 0"
                color="warning"
                icon="mdi:account-off-outline"
                :label="ui?.setup.presentation.pairingDisconnectButton"
                size="xs"
                variant="outline"
                @click="emit('disconnectPeerPresenters')"
              />
            </div>
            <ChallengeWheelPairingCard
              v-if="props.presentationSupported"
              :pairing-code="props.pairingCode"
              :pairing-url="props.pairingUrl"
              :presenter-entry-url="props.presenterEntryUrl"
              @regenerate="emit('regeneratePairingCode')"
            />
          </div>
        </template>

        <template #use-as-display>
          <div class="space-y-4">
            <p class="text-sm text-muted">
              {{ ui?.setup.presentation.useAsDisplayDescription }}
            </p>
            <UButton
              block
              icon="mdi:television-play"
              :label="ui?.setup.presentation.launchDisplayModeButton"
              size="lg"
              @click="emit('launchDisplayMode')"
            />
          </div>
        </template>
      </UTabs>
    </UCard>

    <!-- ── Start Button ──────────────────────────────────── -->
    <div class="flex flex-col items-center gap-3 pt-2">
      <p
        v-if="!canStart"
        class="text-sm text-warning"
      >
        {{ ui?.setup.miniGames.minGamesHint }}
      </p>
      <UButton
        :disabled="!canStart"
        icon="mdi:play-circle"
        :label="ui?.setup.startButton"
        size="xl"
        @click="handleStart"
      />
    </div>
  </div>
</template>
